import React from 'react';
import { TrendingUp, ClipboardList, Truck, BarChart3, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  statusFromChartRows,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

export default function SalesDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.ventes.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/ventes/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم المبيعات' })}
      subtitle={p('subtitle', { defaultValue: 'مؤشرات الإيرادات والطلبات' })}
      icon={TrendingUp}
      accent="from-purple-600 to-violet-800"
      recentTitle={p('recent_orders', { defaultValue: 'آخر الأوامر' })}
      quickLinks={[
        { label: p('link_orders', { defaultValue: 'أوامر العملاء' }), href: '/ventes/commandes', icon: ClipboardList },
        { label: p('link_deliveries', { defaultValue: 'التسليمات' }), href: '/ventes/livraisons', icon: Truck },
        { label: p('link_sales_book', { defaultValue: 'دفتر المبيعات' }), href: '/ventes/livre-des-ventes', icon: TrendingUp },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/ventes/analyses-rapports', icon: BarChart3 },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];
        const { active, inactive } = statusFromChartRows(chartRows, [
          'pending',
          'confirmed',
          'preparing',
          'shipped',
          'delivered',
          'paid',
        ]);

        return {
          hero: {
            label: p('kpi_revenue', { defaultValue: 'إيرادات الشهر' }),
            value: formatBrandMoney(kpis.monthRevenue || 0),
            icon: TrendingUp,
          },
          kpis: withKpiVariants([
            { label: p('kpi_open_orders', { defaultValue: 'أوامر مفتوحة' }), value: kpis.openOrders ?? 0, icon: ClipboardList },
            { label: p('kpi_pending_deliveries', { defaultValue: 'تسليمات معلقة' }), value: kpis.pendingDeliveries ?? 0, icon: Truck },
            { label: p('kpi_conversion', { defaultValue: 'نسبة التحويل %' }), value: `${kpis.conversionRate ?? 0}%`, icon: BarChart3 },
            {
              label: p('kpi_total_orders', { defaultValue: 'إجمالي الأوامر' }),
              value: (kpis.openOrders ?? 0) + (kpis.pendingDeliveries ?? 0),
              icon: Layers,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_pipeline_title', { defaultValue: 'مسار المبيعات' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'أوامر وتسليمات وتحويل' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_open_orders', { defaultValue: 'أوامر مفتوحة' }), value: kpis.openOrders ?? 0 },
                { label: p('kpi_pending_deliveries', { defaultValue: 'تسليمات معلقة' }), value: kpis.pendingDeliveries ?? 0 },
                { label: p('kpi_conversion', { defaultValue: 'نسبة التحويل %' }), value: kpis.conversionRate ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status', { defaultValue: 'حالة الأوامر' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'قيد التنفيذ مقابل مغلقة' }),
              active: active || kpis.openOrders || 0,
              inactive: inactive || 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'توزيع حالات البيع' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'حسب حالة الأمر' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
